import { useAppSelector } from 'src/redux/hooks';
import { AppState } from 'src/redux/store';
import { selectLearningModuleById } from 'src/redux/slices/learningModules';
import { useGetLearningModuleQuery } from 'src/redux/slices/api/su';
import { useClientId, useSelLocale } from 'src/contexts/ClientContext';
import { LearningModuleStateData } from 'src/types/state/learningModule';

export interface UseLearningModuleOptions {
  /**
   * When true, forces a fresh GetModule API call regardless of existing store data.
   * Used under PolyD migration (weblab T1) where the LP response only contains
   * single-locale instanceMap and the full multi-locale data must be fetched separately.
   * RTK Query still deduplicates by cache key, so repeated calls for the same
   * (moduleId, clientId, selLocale) within a session won't cause redundant network requests.
   */
  forceRefetch?: boolean;
}

/**
 * Hook to get learning module data with automatic full data fetching
 *
 * This hook:
 * 1. Gets module from Redux store (may be preview data from topic/search APIs)
 * 2. Checks if full data is available (has instanceMap with content URLs)
 * 3. Automatically fetches full data via GetModule API if needed
 * 4. Returns the most complete data available
 *
 * @param learningModuleId - The learning module ID to fetch
 * @param options - Optional configuration for fetch behavior
 * @returns Object containing module data, loading state, error state, and existence check
 */
const useLearningModule = (
  learningModuleId: string | undefined,
  options?: UseLearningModuleOptions,
) => {
  const { forceRefetch = false } = options || {};
  const clientId = useClientId();
  const selLocale = useSelLocale();
  const moduleFromStore = useAppSelector((state: AppState) => (
    learningModuleId ? selectLearningModuleById(state, learningModuleId) : undefined
  ));

  const hasFullData = (moduleData?: LearningModuleStateData) => {
    return moduleData?.instanceMap && Object.keys(moduleData.instanceMap).length > 0;
  };

  // When forceRefetch is true (PolyD migration T1), always fetch to get full multi-locale
  // instanceMap from GetModule API. RTK Query cache key (moduleId, clientId, selLocale)
  // ensures no redundant network calls for the same module within a session.
  // When selLocale is set, always fetch — the Redux store is keyed by moduleId only,
  // so stored data may be from a different locale. RTK Query deduplicates by
  // (moduleId, clientId, selLocale) cache key, so no redundant network calls.
  const shouldFetch = !!learningModuleId && (forceRefetch || !!selLocale || !hasFullData(moduleFromStore?.data));

  const {
    isLoading,
    isError,
    error,
  } = useGetLearningModuleQuery(
    { moduleId: learningModuleId!, clientId, selLocale },
    { skip: !shouldFetch },
  );

  return {
    /**
     * Always return data from Redux store (never directly from API)
     * The API call populates the store, then we read the shaped data.
     * When selLocale is set and loading, return undefined to avoid showing stale locale data.
     * When forceRefetch is true and loading, return undefined to avoid showing stale/partial data.
     */
    learningModule: ((selLocale || forceRefetch) && isLoading) ? undefined : moduleFromStore?.data,

    /**
     * Loading state - true when fetching full data
     */
    isLoading,

    /**
     * Error state from GetModule API call
     */
    isError,

    /**
     * Error object from GetModule API call
     */
    error,
  };
};

export default useLearningModule;
